import { useCallback, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

import { SchemaHolder } from './schema';
import { QueryParameters } from './types';

export function useSearchHistory<StaticProps, SearchQuery, Parameters extends QueryParameters>(
  staticProps: StaticProps,
  defaultParameters: Parameters,
  schema: SchemaHolder<StaticProps, SearchQuery, Parameters>
) {
  const { search } = useLocation();
  const [, setURLSearchParams] = useSearchParams();

  const urlRnd = useMemo(() => new URLSearchParams(search).get('r'), [search]);
  const push = useCallback(
    (rnd: string, params: Parameters) => {
      const paramsObject: Record<string, string> = {};
      schema.parameters.forEach((parameter) => {
        if (!parameter.toUrlSearchParams) return;
        const value = parameter.getValue({
          parameters: params,
          staticProps,
        });
        const defaultValue = parameter.getDefaultValue({ defaultParameters });
        const result = parameter.toUrlSearchParams({
          value,
          staticProps,
          defaultValue,
          parameters: params,
        });
        if (!result) return;
        Object.assign(paramsObject, result);
      });

      const queryParamString = `?${generateUrl({ ...paramsObject, r: rnd })}`;
      setURLSearchParams(queryParamString);
    },
    [schema.parameters, setURLSearchParams, staticProps, defaultParameters]
  );

  const parse = useCallback(
    () => parseUrl<StaticProps, SearchQuery, Parameters>(search, staticProps, defaultParameters, schema),
    [defaultParameters, schema, search, staticProps]
  );

  return { urlRnd, push, parse };
}

function parseUrl<StaticProps, SearchQuery, Parameters extends QueryParameters>(
  search: string,
  staticProps: StaticProps,
  defaultParameters: Parameters,
  schema: SchemaHolder<StaticProps, SearchQuery, Parameters>
) {
  const urlSearchParams = new URLSearchParams(search);

  const result = { ...defaultParameters };

  schema.parameters.forEach((parameter) => {
    if (!parameter.fromUrlSearchParams) return;
    const params = parameter.fromUrlSearchParams({
      staticProps,
      urlSearchParams: urlSearchParams,
      defaultValue: parameter.getDefaultValue({ defaultParameters: defaultParameters }),
    });
    if (!params) return;
    Object.assign(result, params);
  }, {} as QueryParameters);
  return result;
}

const generateUrl = (paramsObject: Record<string, string>) => {
  return Object.entries(paramsObject)
    .filter(([, v]) => !!v)
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
};
